import React, {useEffect, useCallback, useMemo, useState} from "react";
import {Button, Col, Form, Modal, Row, Tab, Tabs, Dropdown, Alert} from "react-bootstrap";
import {v4 as uuidv4} from "uuid";
import {GripVertical, Plus, Trash} from "react-bootstrap-icons";
import {useNavigate} from "react-router-dom";
import {isFulfilled} from "@reduxjs/toolkit";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {
    changeModal,
    closeModal, deleteForm,
    EditFormPayload,
    getForm,
    NewFormPayload,
    saveForm,
    saveNewForm,
    setActiveLanguage
} from "../../../app/slices/modals/formModal";
import {ADMIN_LANG, AVAILABLE_LANGUAGES} from "../../../settings";
import AdminLoader from "../AdminLoader";
import {Field} from "../../../types/admin";
import Confirm from "../Confirm";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";

const FormModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        isOpen,
        loading,
        form_id,
        languages,
        languageTab,
        form_fields,
        error,
        game_id,
        game_form,
        delete_message
    } = useAppSelector((state) => state.formModal);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    // Fetch form data if form_id is present
    useEffect(() => {
        if (form_id) {
            dispatch(getForm(form_id));
        }
    }, [dispatch, form_id]);

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            languages,
            form_fields,
            ...(form_id && {form_id}),
            ...(game_id && {game_id}),
            ...(game_form && {game_form}),
        };

        const action = form_id ? saveForm(payload as EditFormPayload) : saveNewForm(payload as NewFormPayload);

        dispatch(action).then((resultAction) => {
            if (!form_id && !game_id && isFulfilled(resultAction)) {
                let newId = '0'
                if(resultAction.payload && resultAction.payload.form_id){
                    newId = resultAction.payload.form_id
                }
                navigate(`/admin/forms/?new=${newId}`);
            }
        });
    };

    const handleDelete = () => {
        setShowConfirmDelete(true)
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (form_id) {
            dispatch(deleteForm(form_id)).then((resultAction) => {
                if (isFulfilled(resultAction)) {
                    navigate('/admin/forms');
                }
            });
        }
    };

    const handleClose = useCallback(() => {
        dispatch(closeModal());
    }, [dispatch]);

    const handleTabSelect = useCallback((key: string | null) => {
        if (key) {
            dispatch(setActiveLanguage(key));
        }
    }, [dispatch]);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedLanguages = languages.map((language) =>
            language.language_code === languageTab
                ? {...language, name: e.target.value}
                : language
        );

        if (!languages.some(language => language.language_code === languageTab)) {
            updatedLanguages.push({language_code: languageTab, name: e.target.value});
        }

        dispatch(changeModal({languages: updatedLanguages}));
    }, [dispatch, languages, languageTab]);

    const handleFieldChange = useCallback((id: string, changes: Partial<Field>) => {
        const updatedFields = form_fields.map(field =>
            field.field_id === id ? {...field, ...changes} : field
        );
        dispatch(changeModal({form_fields: updatedFields}));
    }, [dispatch, form_fields]);

    const handleAddField = useCallback((fieldType: string) => {
        const newField = {
            field_id: uuidv4(),
            key: uuidv4(),
            required: fieldType !== 'custom_text',
            disabled: fieldType !== 'custom_text',
            type: fieldType,
            languages: AVAILABLE_LANGUAGES.map(lang => ({
                language_code: lang,
                label: fieldType === 'email' ? 'Sähköposti' : (fieldType === 'name' ? 'Nimi' : ''),
            }))
        };
        dispatch(changeModal({form_fields: [...form_fields, newField]}));
    }, [dispatch, form_fields]);

    const handleRemoveField = useCallback((id: string) => {
        dispatch(changeModal({form_fields: form_fields.filter(field => field.field_id !== id)}));
    }, [dispatch, form_fields]);

    const onDragEnd = useCallback(
        (result: any) => {
            if (!result.destination) {
                return;
            }

            const reorderedFields = Array.from(form_fields);
            const [removed] = reorderedFields.splice(result.source.index, 1);
            reorderedFields.splice(result.destination.index, 0, removed);

            dispatch(changeModal({form_fields: reorderedFields}));
        },
        [form_fields, dispatch]
    );

    const renderFields = useMemo(
        () => (
            <Droppable droppableId="fields-list">
                {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                        {form_fields.map((field, index) => (
                            <Draggable key={field.key} draggableId={field.key} index={index}>
                                {(provided) => (
                                    <Row
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className="gy-3 pb-3"
                                    >
                                        <Col xs={7}>
                                            <Form.Group controlId={`field_label_${field.type}_${field.key}_${languageTab}`}>
                                                <Form.Control
                                                    type="text"
                                                    maxLength={255}
                                                    value={
                                                        field.languages.find(lang => lang.language_code === languageTab)?.label ||
                                                        ''
                                                    }
                                                    onChange={(e) =>
                                                        handleFieldChange(field.field_id, {
                                                            languages: field.languages.map((fl) =>
                                                                fl.language_code === languageTab
                                                                    ? { ...fl, label: e.target.value }
                                                                    : fl
                                                            ),
                                                        })
                                                    }
                                                    disabled={field.disabled}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                            <Form.Select
                                                value={field.type}
                                                onChange={(e) =>
                                                    handleFieldChange(field.field_id, { type: e.target.value })
                                                }
                                                disabled={field.disabled}
                                            >
                                                <option hidden value="name">
                                                    {ADMIN_LANG.text}
                                                </option>
                                                <option hidden value="email">
                                                    {ADMIN_LANG.email}
                                                </option>
                                                <option value="custom_text">{ADMIN_LANG.text}</option>
                                                <option value="custom_email">{ADMIN_LANG.email}</option>
                                                <option value="custom_checkbox">{ADMIN_LANG.checkbox}</option>
                                            </Form.Select>
                                        </Col>
                                        <Col xs={1} className="d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                label=""
                                                checked={field.required}
                                                onChange={(e) =>
                                                    handleFieldChange(field.field_id, { required: e.target.checked })
                                                }
                                            />
                                        </Col>
                                        <Col xs={1} className="d-flex justify-content-center align-items-center">
                                            <Trash onClick={() => handleRemoveField(field.field_id)} className="utility-icon red" />
                                            <GripVertical className="utility-icon ms-2" />
                                        </Col>
                                    </Row>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        ),
        [form_fields, languageTab, handleFieldChange, handleRemoveField]
    );

    return (
        <Modal size="xl" backdropClassName={game_id ? "nested-backdrop" : ""} show={isOpen} onHide={handleClose} className="admin-modal">
            {loading && <AdminLoader />}
            <Modal.Header closeButton>
                <Modal.Title>{form_id ? ADMIN_LANG.edit_form : ADMIN_LANG.new_form}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="container-fluid">
                {error && (
                    <Alert variant="danger" dismissible>
                        <div dangerouslySetInnerHTML={{ __html: error }} />
                    </Alert>
                )}
                <Form id="form-modal" onSubmit={handleSave}>
                    <Tabs activeKey={languageTab} onSelect={handleTabSelect}>
                        {AVAILABLE_LANGUAGES.map((language) => (
                            <Tab key={language} eventKey={language} title={language}>
                                <Row className="pt-3 gy-3">
                                    <Form.Group as={Col} controlId={`form_title_${language}`}>
                                        <Form.Label>{`${ADMIN_LANG.name} (${language.toUpperCase()})`}</Form.Label>
                                        <Form.Control
                                            maxLength={255}
                                            type="text"
                                            value={languages.find((lang) => lang.language_code === language)?.name || ""}
                                            onChange={handleNameChange}
                                        />
                                    </Form.Group>

                                    <Col xs={12}>
                                        <Dropdown onSelect={(eventKey) => handleAddField(eventKey as string)}>
                                            <Dropdown.Toggle variant="success" id="dropdown-basic">
                                                <Plus size="1.5rem" className="me-1" />
                                                {ADMIN_LANG.add_field}
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                                <Dropdown.Item eventKey="name">{ADMIN_LANG.name_field}</Dropdown.Item>
                                                <Dropdown.Item eventKey="email">{ADMIN_LANG.email_field}</Dropdown.Item>
                                                <Dropdown.Item eventKey="custom_text">{ADMIN_LANG.custom_field}</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Col>

                                    <Col xs={12}>
                                        <Row>
                                            <Col xs={7}>
                                                <Form.Label>{ADMIN_LANG.field_label}</Form.Label>
                                            </Col>
                                            <Col xs={3}>
                                                <Form.Label>{ADMIN_LANG.field_type}</Form.Label>
                                            </Col>
                                            <Col xs={1}>
                                                <Form.Label>{ADMIN_LANG.field_required}</Form.Label>
                                            </Col>
                                        </Row>
                                        <DragDropContext onDragEnd={onDragEnd}>{renderFields}</DragDropContext>
                                    </Col>
                                </Row>
                            </Tab>
                        ))}
                    </Tabs>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {form_id && !game_id ? (
                    <Button variant="danger" className="me-auto" onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                ) : null}
                <Button variant="outline-primary" onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                {!(game_id && form_id) && (
                    <Button form="form-modal" type="submit" variant="primary">
                        {ADMIN_LANG.save}
                    </Button>
                )}
            </Modal.Footer>
            <Confirm show={showConfirmDelete} message={delete_message} onConfirm={handleConfirm} onCancel={() => setShowConfirmDelete(false)} />
        </Modal>
    );
};

export default FormModal;